import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Heading,
  InlineAlert,
  InputText,
  Modal,
} from '@design-systems/canvas-ui'
import Icon, { ArrowLeftIcon } from '@design-systems/react-icons'
import { getBrandDetailsPath } from '../../../shared/routes/routes'
import ChatComposer from '../components/ChatComposer'
import ChatTranscript from '../components/ChatTranscript'
import ConversationSidebar from '../components/ConversationSidebar'
import { useChatAssistant } from '../hooks/useChatAssistant'
import './AssistantPage.css'

const formatBrandName = (slug) =>
  `${slug || ''}`
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => `${word[0].toUpperCase()}${word.slice(1)}`)
    .join(' ')

const AssistantPage = () => {
  const navigate = useNavigate()
  const chat = useChatAssistant()
  const [isRenameOpen, setIsRenameOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [renameTitle, setRenameTitle] = useState('')
  const [isUpdatingTitle, setIsUpdatingTitle] = useState(false)
  const [isDeletingConversation, setIsDeletingConversation] = useState(false)
  const messagesEndRef = useRef(null)
  const conversationRef = useRef(null)
  const scrollAdjustmentRef = useRef(null)
  const userScrollUpRef = useRef(false)
  const touchStartYRef = useRef(null)
  const wasLoadingConversationRef = useRef(false)
  const activeBrand = useMemo(
    () =>
      chat.session?.brand
        ? {
            name: formatBrandName(chat.session.brand),
            slug: chat.session.brand,
          }
        : null,
    [chat.session?.brand],
  )
  const requestedBrandName = useMemo(
    () => formatBrandName(chat.brandSlug || ''),
    [chat.brandSlug],
  )
  const brandLabel = activeBrand?.name || requestedBrandName || ''

  useLayoutEffect(() => {
    if (scrollAdjustmentRef.current && conversationRef.current) {
      const { scrollHeight, scrollTop } = scrollAdjustmentRef.current
      conversationRef.current.scrollTop =
        scrollTop + conversationRef.current.scrollHeight - scrollHeight
      scrollAdjustmentRef.current = null
      return
    }
    const justLoadedConversation =
      wasLoadingConversationRef.current && !chat.isLoadingConversation
    wasLoadingConversationRef.current = chat.isLoadingConversation

    if (justLoadedConversation || chat.isSending) {
      messagesEndRef.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'end',
      })
    }
  }, [chat.exchanges, chat.isLoadingConversation, chat.isSending])

  const handleConversationScroll = async (event) => {
    const container = event.currentTarget
    if (
      userScrollUpRef.current &&
      container.scrollTop <= 80 &&
      chat.olderCursor &&
      !chat.isLoadingOlder
    ) {
      userScrollUpRef.current = false
      scrollAdjustmentRef.current = {
        scrollHeight: container.scrollHeight,
        scrollTop: container.scrollTop,
      }
      const loaded = await chat.loadOlderExchanges()
      if (!loaded) scrollAdjustmentRef.current = null
    }
  }

  const isEmptyConversation = !chat.session && !chat.exchanges.length
  const shouldShowBackButton = Boolean(
    chat.brandSlug || chat.session || chat.selectedSessionId,
  )

  const handleBackNavigation = () => {
    const brandPath = chat.brandSlug || chat.session?.brand

    if (chat.session || chat.selectedSessionId) {
      chat.startNewConversation()
      if (brandPath) {
        navigate(getBrandDetailsPath(brandPath), { replace: true })
      } else {
        navigate(-1)
      }
      return
    }

    if (brandPath) {
      navigate(getBrandDetailsPath(brandPath), { replace: true })
    }
  }

  const handleRename = async () => {
    setIsUpdatingTitle(true)
    try {
      const renamed = await chat.renameConversation(renameTitle)
      if (renamed) setIsRenameOpen(false)
    } finally {
      setIsUpdatingTitle(false)
    }
  }

  const handleDelete = async () => {
    setIsDeletingConversation(true)
    try {
      const deleted = await chat.deleteConversation()
      if (deleted) setIsDeleteOpen(false)
    } finally {
      setIsDeletingConversation(false)
    }
  }

  const handleOpenRenameModal = () => {
    if (!chat.session) return
    setRenameTitle(chat.session.title || '')
    setIsRenameOpen(true)
  }

  const handleOpenDeleteModal = () => {
    if (!chat.session) return
    setIsDeleteOpen(true)
  }

  const { listStatus, loadSessions, sessionCursor } = chat
  const loadMoreSessions = useCallback(
    () => loadSessions(listStatus, sessionCursor, true),
    [listStatus, loadSessions, sessionCursor],
  )

  return (
    <section className="assistant" aria-label="Brand DNA Assistant">
      <ConversationSidebar
        error={chat.listError}
        isLoading={chat.isLoadingSessions}
        listStatus={chat.listStatus}
        onArchiveSession={chat.changeArchiveStatus}
        onDeleteSession={handleOpenDeleteModal}
        onLoadMore={loadMoreSessions}
        onNewChat={chat.startNewConversation}
        onOpenSession={chat.openConversation}
        onRenameSession={handleOpenRenameModal}
        onStatusChange={chat.setListStatus}
        selectedSessionId={chat.selectedSessionId}
        sessionCursor={chat.sessionCursor}
        sessions={chat.sessions}
      />

      <main className="assistant__main">
        {!isEmptyConversation && (
          <header className="assistant__header">
            <div className="assistant__header-main">
              {shouldShowBackButton && (
                <Button
                  aria-label="Go back"
                  className="assistant__back-button"
                  onClick={handleBackNavigation}
                  size="sm"
                  type="button"
                  variant="ghost-neutral"
                >
                  <Icon aria-hidden="true" svg={ArrowLeftIcon} />
                </Button>
              )}
              <div>
                <Heading as="h1" size={4}>
                  {chat.session?.title ||
                    (chat.session?.brand
                      ? formatBrandName(chat.session.brand)
                      : '')}
                </Heading>
              </div>
            </div>
          </header>
        )}

        <section
          className="assistant__conversation"
          aria-label="Chat messages"
          aria-live="polite"
          aria-busy={chat.isSending || chat.isLoadingConversation}
          onScroll={handleConversationScroll}
          onWheel={(event) => {
            userScrollUpRef.current = event.deltaY < 0
          }}
          onTouchStart={(event) => {
            touchStartYRef.current = event.touches[0]?.clientY ?? null
          }}
          onTouchMove={(event) => {
            const touchY = event.touches[0]?.clientY
            if (touchY != null && touchStartYRef.current != null) {
              userScrollUpRef.current = touchY > touchStartYRef.current
              touchStartYRef.current = touchY
              if (userScrollUpRef.current) handleConversationScroll(event)
            }
          }}
          onTouchEnd={() => {
            touchStartYRef.current = null
          }}
          ref={conversationRef}
        >
          {isEmptyConversation && !chat.isLoadingConversation ? (
            <div className="assistant__welcome">
              <Heading as="h2" size={2}>
                What can I help you with?
              </Heading>
              <p>
                Plan a brand strategy, confirm a brand guardrail, review copy &
                creative. Ask across Target's portfolio, or pick a brand.
              </p>
              <div className="assistant__prompts">
                {[
                  'Tell me about Target’s master brand voice and tone.',
                  'What are the Target rules for the Bullseye, Target Red, and trademark?',
                  'What are Target’s inclusive marketing standards?',
                ].map((prompt) => (
                  <Button
                    key={prompt}
                    onClick={() => chat.setDraft(prompt)}
                    type="button"
                    variant="secondary"
                  >
                    {prompt}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <ChatTranscript
              activeAction={chat.activeAction}
              exchanges={chat.exchanges}
              isLoadingConversation={chat.isLoadingConversation}
              isLoadingOlder={chat.isLoadingOlder}
              olderCursor={chat.olderCursor}
              onLoadOlder={chat.loadOlderExchanges}
              onRate={chat.rateExchange}
              onRetry={chat.retryExchange}
            />
          )}
          <div ref={messagesEndRef} />
        </section>

        <div className="assistant__composer-area">
          {chat.chatError && (
            <InlineAlert
              className="assistant__error"
              heading="Chat needs attention"
              variant="error"
            >
              {chat.chatError}
            </InlineAlert>
          )}
          <ChatComposer
            draft={chat.draft}
            hasSession={Boolean(chat.session)}
            isSending={chat.isSending || chat.isLoadingConversation}
            onDraftChange={chat.setDraft}
            onSubmit={chat.sendMessage}
            sessionBrand={
              chat.session?.brand
                ? brandLabel
                : chat.brandSlug
                  ? requestedBrandName
                  : ''
            }
            textareaRef={chat.textareaRef}
          />
        </div>
      </main>
      <Modal
        footer={
          <div className="assistant__modal-actions">
            <Button
              isDisabled={isUpdatingTitle}
              onClick={() => setIsRenameOpen(false)}
              type="button"
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              isDisabled={!renameTitle.trim() || renameTitle.length > 200}
              isLoading={isUpdatingTitle}
              onClick={handleRename}
              type="button"
              variant="primary"
            >
              Save name
            </Button>
          </div>
        }
        headingText="Rename conversation"
        isOpen={isRenameOpen}
        onClose={() => setIsRenameOpen(false)}
        size="sm"
      >
        <label
          className="assistant__rename-field"
          htmlFor="assistant-session-title"
        >
          <span>Conversation name</span>
          <InputText
            autoFocus
            id="assistant-session-title"
            maxLength={200}
            onChange={(event) => setRenameTitle(event.target.value)}
            value={renameTitle}
          />
        </label>
      </Modal>
      <Modal
        footer={
          <div className="assistant__modal-actions">
            <Button
              isDisabled={isDeletingConversation}
              onClick={() => setIsDeleteOpen(false)}
              type="button"
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              isLoading={isDeletingConversation}
              onClick={handleDelete}
              type="button"
              variant="destructive"
            >
              Delete conversation
            </Button>
          </div>
        }
        headingText="Delete conversation?"
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        size="sm"
      >
        <p>
          This permanently deletes the conversation and all of its messages.
        </p>
      </Modal>
    </section>
  )
}

export default AssistantPage
